import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    Injector,
    OnInit,
    Signal,
    ViewChild,
    ViewEncapsulation,
    signal,
    computed,
    untracked,
} from '@angular/core';
import { IOnboardingActive, IOnboardingActiveData } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { IItemsListSort } from '@fi-shared/fi-items-list/fi-items-list';
import { IItemsListSortOption } from '@fi-app/shared/fi-items-list/fi-items-list-sort-select/fi-items-list-sort-select.component';
import { FiOnboardingActiveHelperService } from '../fi-onboarding-active-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { ILocalSearchParameter } from '@fi-shared/models/models';
import { Observable, ReplaySubject } from 'rxjs';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { FiLocalSearch2Component } from '@fi-shared/fi-local-search2/fi-local-search2.component';
import { FiLocalSearch2HelperService } from '@fi-shared/fi-local-search2/fi-local-search2-helper.service';
import { FiOnboardingActiveDataService } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-data.service';
import { IOnboardingAssigneesItem } from '@fi-app/fi-onboarding/fi-onboarding-synchronized/fi-onboarding-synchronized';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { FiOnboardingHelperService } from '../../fi-onboarding-helper.service';

@Component({
    selector: 'fi-onboarding-active-list',
    templateUrl: './fi-onboarding-active-list.component.html',
    styleUrl: './fi-onboarding-active-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-active-list' },
    standalone: false,
})
export class FiOnboardingActiveListComponent extends FiInfiniteScrollDataSource<IOnboardingActive> implements OnInit {
    private fiCustomerDataService = inject(FiCustomerDataService);
    private fiLocalSearch2HelperService = inject(FiLocalSearch2HelperService);
    private fiOnboardingActiveDataService = inject(FiOnboardingActiveDataService);
    private fiOnboardingHelperService = inject(FiOnboardingHelperService);

    @ViewChild(FiLocalSearch2Component) localSearch2Component: FiLocalSearch2Component;

    skipGetSortDataFromLocalStorage = true;
    //defaultSortColumnName = 'CompletedFraction';
    //defaultSortDirection = false;
    public readonly objectType = ObjectTypes.Onboarding;
    public readonly itemsListType = FiItemsListType.OnboardingActive;

    protected selectedStatus: FiOnboardingStatusType;

    private readonly fiOnboardingActiveHelperService = inject(FiOnboardingActiveHelperService);
    private readonly fiStateService = inject(FiStateService);
    protected readonly sideListOptions = this.fiOnboardingActiveHelperService.sideListOptions;
    protected allOnboardingsCounter$: Observable<number> = this.sideListOptions.find(
        (option) => option.value === undefined,
    )?.counter$ as Observable<number>;

    protected sortOptions: IItemsListSortOption[] = [];

    protected onboardingActiveData = computed<IOnboardingActiveData>(() =>
        this.fiOnboardingActiveDataService.onboardingActiveData(),
    );
    protected localSearchParameter: ILocalSearchParameter;

    private _lightWeightObjectCollection = signal<ILightWeightObjectCollection | undefined>(undefined);
    public lightWeightObjectCollection = this._lightWeightObjectCollection.asReadonly();

    protected onboardingAssignees: IOnboardingAssigneesItem[] = [];

    private savedItemsListSort: {
        [prop: number]: IItemsListSort;
    } = {};

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        const params = this.fiStateService.params;
        if (isDefined(params.selectedStatus)) {
            this.selectedStatus = coerceNumberProperty(params.selectedStatus);
        }

        const sortByStatus = this.fiOnboardingActiveHelperService.getSortByStatus(this.selectedStatus);
        this.defaultSortColumnName = isDefined(params.sortColumn) ? params.sortColumn : sortByStatus.ColumnName;
        this.defaultSortDirection = isDefined(params.sortDirection)
            ? coerceBooleanProperty(params.sortDirection)
            : sortByStatus.SortDirection;

        this.saveSelectedSortForStatus(
            {
                ColumnName: this.defaultSortColumnName,
                SortDirection: this.defaultSortDirection,
            },
            this.selectedStatus,
        );

        this.sortOptions = this.fiOnboardingActiveHelperService.getSortOptionsByStatus(this.selectedStatus);
        this.fiOnboardingActiveDataService.reloadOnboardingActiveData(); // todo not optimal
        this.fiOnboardingHelperService.setOnboardingsCount();
    }

    private readonly onboardingActiveDataEffectRef = effect(() => {
        const onboardingActiveData = this.onboardingActiveData();
        this.onboardingAssignees = onboardingActiveData.OnboardingAssignees;
        this.reloadData().then(() => {
            this.setLightWeightObjectsForLocalSearch();
        });
    });

    protected showOnboardingAssignees() {
        return this.onboardingAssignees.length > 0 && this.fiCustomerDataService.isSynchronizedOnboardingEnabled();
    }

    public getListData(): Promise<IOnboardingActive[]> {
        const isLocalSearchEmpty = !isDefined(this.localSearch2Component) || this.localSearch2Component.isEmpty();
        let isEmptyFilter: boolean = !isDefined(this.selectedStatus) && isLocalSearchEmpty;
        const activeOnboardings = this.onboardingActiveData().ActiveOnboardings;
        const filteredOnboardings = isEmptyFilter
            ? [...activeOnboardings]
            : activeOnboardings.filter((onboarding) => {
                  let result = true;
                  if (isDefined(this.selectedStatus)) {
                      result = onboarding.Status === this.selectedStatus;
                  }
                  if (!isLocalSearchEmpty && result) {
                      result =
                          this.fiLocalSearch2HelperService.isNameMatching(onboarding.Name, this.localSearchParameter) &&
                          this.fiLocalSearch2HelperService.isCreatedByMatching(
                              onboarding.CreatedByUserName,
                              this.localSearchParameter,
                          );
                  }
                  return result;
              });

        return Promise.resolve(filteredOnboardings);
    }

    protected onOnboardingClick(onboardingId: number) {
        this.fiStateService.go(FiStateNames.Onboarding.Completion, { id: onboardingId });
    }

    protected selectedSortChange($event: IItemsListSort) {
        const sortData = this.getSortData($event.ColumnName, $event.SortDirection);
        super.onSortChanged(sortData);

        // https://chainformation.atlassian.net/browse/FI-6909?focusedCommentId=101752
        // store sort for current status for restoring it in future
        this.saveSelectedSortForStatus($event, this.selectedStatus);

        this.updateStateParams();
    }

    private getStatusForStoringSelectedSort(status: FiOnboardingStatusType): number {
        if (isDefined(status)) {
            return status;
        }
        return -1;
    }

    private saveSelectedSortForStatus(itemsListSort: IItemsListSort, status: FiOnboardingStatusType) {
        this.savedItemsListSort[this.getStatusForStoringSelectedSort(status)] = itemsListSort;
    }

    protected selectedStatusChanged(status: FiOnboardingStatusType) {
        this.selectedStatus = status;
        this.sortOptions = this.fiOnboardingActiveHelperService.getSortOptionsByStatus(this.selectedStatus);

        // https://chainformation.atlassian.net/browse/FI-6909?focusedCommentId=101752
        // restore previous sort for given status (if exists)
        const savedItemsListSort = this.savedItemsListSort[this.getStatusForStoringSelectedSort(this.selectedStatus)];
        const itemsListSort = isDefined(savedItemsListSort)
            ? savedItemsListSort
            : this.fiOnboardingActiveHelperService.getSortByStatus(this.selectedStatus);

        const newSortData = this.getSortData(itemsListSort.ColumnName, itemsListSort.SortDirection);
        this.updateCurrentSort(newSortData);

        this.updateStateParams();

        this.reloadData().then(() => {
            this.setLightWeightObjectsForLocalSearch();
        });
    }

    private updateStateParams() {
        this.fiStateService.go(
            this.fiStateService.current.name,
            {
                selectedStatus: this.selectedStatus,
                sortColumn: this.sortColumn,
                sortDirection: this.sortDirection,
            },
            {
                location: 'replace',
            },
        );
    }

    protected setLightWeightObjectsForLocalSearch() {
        const lightWeightObjectsForLocalSearch: ILightWeightObjectCollection = cloneDeep(
            this.onboardingActiveData().LightWeightObjectsForLocalSearch,
        );
        if (!isDefined(lightWeightObjectsForLocalSearch)) {
            return;
        }
        if (isDefined(this.selectedStatus)) {
            lightWeightObjectsForLocalSearch.Names = lightWeightObjectsForLocalSearch.Names.filter(
                (localSearchName) => {
                    return !!this.items().find((item) => localSearchName.Name === item.Name);
                },
            );
            lightWeightObjectsForLocalSearch.Creators = lightWeightObjectsForLocalSearch.Creators.filter(
                (localSearchCreator) => {
                    return !!this.items().find((item) => localSearchCreator.Name === item.CreatedByUserName);
                },
            );
        }
        this._lightWeightObjectCollection.set(lightWeightObjectsForLocalSearch);
    }

    protected onSearchChanged($event: ILocalSearchParameter) {
        this.reloadData();
    }
}
